import { Form, FormInstance, InputNumber, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import { Kind, Order, OrderType } from "../../types/order.type";
import useGetRequest from "../../hooks/useGetRequest";
import { Material } from "../../types/material.type";
import { Produce } from "../../types/produce.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { lom, sim  , durAlm} from "../../config/config";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Option } = Select;

interface CreateOrderProps {
  title: string;
  open: boolean;
  form: FormInstance;
  confirmLoading: boolean;
  onCancel: () => void;
  onOk: () => void;
  onFinish: (values: any) => void;
  clientType: string;
}

const CreateOrder: React.FC<CreateOrderProps> = ({
  title,
  open,
  form,
  confirmLoading,
  onCancel,
  onOk,
  onFinish,
  clientType,
}) => {
  const defaultKindId = clientType === "Лом" ? lom : null;
  const defaultKind = clientType === "Лом" ? Kind.MATERIAL : null;
  const defaultType =
    clientType === "Продажа" ? OrderType.SALE : OrderType.PURCHASE;
  const [selectedKind, setSelectedKind] = useState<Kind | null>(defaultKind);
  const [totalCost, setTotalCost] = useState({
    USD: 0,
    TJS: 0,
  });
  const [discount, setDiscount] = useState(0);
  const { exchangeRate } = useExchangeRate();

  const { data: producesList, loading: produceLoading } =
    useGetRequest("/produce/list");
  const { data: materialsList, loading: materialLoading } =
    useGetRequest("/material/list");
  const { data: machinesList, loading: machineLoading } =
    useGetRequest("/machine/list");

  const handleKindChange = (value: Kind) => {
    setSelectedKind(value);
    form.setFieldsValue({ kindId: null });
  };

  const calculateTotalCost = (values: any) => {
    const { currency, total, price, discount } = values;

    if (
      !currency ||
      total === undefined ||
      price === undefined ||
      !exchangeRate
    )
      return;

    let costInUSD = total * price;

    if (discount && defaultType === OrderType.SALE) {
      costInUSD = costInUSD - (costInUSD * discount) / 100;
    }

    if (currency === Currency.US_DOLLAR) {
      setTotalCost({
        USD: costInUSD,
        TJS: costInUSD * exchangeRate,
      });
    } else {
      setTotalCost({
        USD: costInUSD / exchangeRate,
        TJS: costInUSD,
      });
    }
  };

  const getKindOptions = () => {
    switch (selectedKind) {
      case Kind.PRODUCE:
        return producesList?.map((produce: Produce) => (
          <Option key={produce._id} value={produce._id}>
            {produce.name} ({produce.unitOfMeasurement})
          </Option>
        ));
      case Kind.MATERIAL:
        return materialsList
          ?.filter((material: Material) => {
            if (clientType === "Лом") {
              return material._id === lom || material._id === sim || material._id === durAlm;
            }
            return true;
          })
          .map((material: Material) => (
            <Option key={material._id} value={material._id}>
              {material.name} ({material.unitOfMeasurement})
            </Option>
          ));
      default:
        return null;
    }
  };

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      exchangeRate: exchangeRate || values.exchangeRate,
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        onValuesChange={(_, allValues) => calculateTotalCost(allValues)}
      >
        <Form.Item
          name="type"
          label="Тип заказа"
          rules={[{ required: true, message: "Выберите тип заказа" }]}
          initialValue={defaultType}
        >
          <Select
            placeholder="Выберите тип заказа"
            value={defaultType}
            disabled={true}
          >
            {Object.values(OrderType).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="kind"
          label="Тип сущ."
          rules={[{ required: true, message: "Выберите тип сущ." }]}
          initialValue={defaultKind}
        >
          <Select
            placeholder="Выберите тип сущ."
            onChange={handleKindChange}
            disabled={defaultKind !== null}
          >
            {Object.values(Kind).map((kind) => (
              <Option key={kind} value={kind}>
                {kind}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="kindId"
          label="Элемент"
          rules={[{ required: true, message: "Выберите элемент" }]}
          initialValue={clientType === "Лом" ? lom : defaultKindId}
        >
          <Select
            placeholder="Выберите элемент"
            loading={produceLoading || materialLoading}
            showSearch
            filterOption={(input, option) => {
              const optionText = option?.label || option?.children;
              return String(optionText).toLowerCase().includes(input.toLowerCase());
            }}
            optionFilterProp="children"
          >
            {getKindOptions()}
          </Select>
        </Form.Item>

        {selectedKind === Kind.PRODUCE && defaultType === OrderType.SALE && (
          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
            initialValue={defaultKind}
          >
            <Select 
              placeholder="Выберите станок"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machinesList.map((machine: Machine) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="currency"
          label="Валюта"
          rules={[{ required: true, message: "Выберите валюту" }]}
        >
          <Select placeholder="Выберите валюту">
            {Object.values(Currency).map((currency) => (
              <Option key={currency} value={currency}>
                {currency}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="total"
          label="Количество"
          rules={[{ required: true, message: "Введите количество" }]}
        >
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        {selectedKind === Kind.PRODUCE && (
          <Form.Item
            name="totalKg"
            label="Количество км"
            rules={[{ required: true, message: "Введите количество км" }]}
          >
            <InputNumber style={{ width: 150 }} />
          </Form.Item>
        )}

        <Form.Item
          name="price"
          label="Цена"
          rules={[{ required: true, message: "Введите цену" }]}
        >
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        <Form.Item
          name="exchangeRate"
          label="Курс"
          hidden={true}
        >
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        {defaultType === OrderType.SALE && (
          <Form.Item
            name="discount"
            label="Скидка (%)"
            rules={[{ required: false, message: "Введите скидку" }]}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: 150 }}
              onChange={(value) => setDiscount(value || 0)}
            />
          </Form.Item>
        )}

        <div style={{ marginTop: 16, fontWeight: "bold" }}>
          Общая стоимость:
          <br />
          USD: {totalCost.USD.toFixed(2)}
          <br />
          TJS: {totalCost.TJS.toFixed(2)}
        </div>
      </Form>
    </Modal>
  );
};

export default CreateOrder;
